import {
  Button,
  ButtonWithConfirmation,
  Collapse,
  Form,
  Input,
} from '@readcloud/component-library';
import {
  Annotation,
  PDFTextHighlightAnnotation,
  TextHighlightAnnotation,
  User,
  UserState,
} from '@readcloud/data';
import C from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { IconOfAnnotation } from './IconOfAnnotation';

import { Comment } from '@readcloud/data';
import {
  MdCloud,
  MdComment,
  MdDelete,
  MdEdit,
  MdPerson,
  MdVisibility,
  MdVisibilityOff,
} from 'react-icons/md';
import { ConnectedUserComment } from '../Comments/ConnectedUserComment';
import { BlockQuote, P, Small, Subtle } from '@readcloud/ui/typography';

interface IAnnotationItemProps {
  anno: Annotation;
  onClick?: (anno: Annotation) => void;
  onEditAnnotation: (anno: Annotation) => void;
  onDeleteAnnotation: (annoId: string) => void;
  toggleAnnotationCollapsed?: (annoId: string) => void;
  authorMap: { [authorId: string]: User };
  onCommentTextChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onSubmitComment: () => void;
  newCommentText: string;
  comments: Comment[];
  user: UserState;
}

export const AnnotationItem: React.FC<IAnnotationItemProps> = ({
  anno,
  authorMap,
  ...props
}) => {
  const [commentsOpen, setCommentsOpen] = React.useState<boolean>(false);

  const { text, selectedText } =
    (anno as PDFTextHighlightAnnotation | TextHighlightAnnotation) || {};

  return (
    <div className="rc-flex rc-flex-col rc-w-full rc-h-full">
      <div
        className={C(
          'rc-flex rc-flex-col rc-items-stretch rc-overflow-hidden rc-w-full',
          'annotation-highlight-item',
          anno?.type === 'Text' && 'highlight-border rc-pr-2'
        )}
        style={{ borderColor: anno?.color }}
      >
        <div
          className={C(
            'rc-w-full rc-p-3',
            props.onClick && 'rc-cursor-pointer'
          )}
          onClick={() => props.onClick && anno && props.onClick(anno)}
        >
          <div className="rc-flex rc-items-center rc-w-full rc-justify-between">
            <div className="btn-icon">
              <IconOfAnnotation type={anno?.type} size="28" />
            </div>
            <div className="rc-px-2" />
            <div className="rc-flex rc-flex-col rc-w-full">
              <div className="rc-flex rc-flex-row rc-justify-between rc-items-center rc-w-full">
                <Small>
                  {authorMap[anno?.author]?.firstName}{' '}
                  {authorMap[anno?.author]?.lastName}
                </Small>
                <Subtle>
                  {moment
                    .duration(moment(anno?.updated).diff(Date.now()))
                    .humanize() + ' ago'}
                </Subtle>
              </div>
              <div>
                <Subtle>Page: {anno?.location?.pageNumber}</Subtle>
              </div>
            </div>
          </div>

          <BlockQuote className="rc-truncate rc-w-full">
            {selectedText}
          </BlockQuote>
          {text && (
            <>
              <P>{text}</P>
            </>
          )}
          <div className="rc-py-3" />
          <div className="rc-flex rc-justify-between rc-items-center rc-w-full vsl-icon">
            <div className="rc-flex rc-items-end">
              {anno?.visibleInClouds?.length || anno?.visibleToUsers?.length ? (
                <>
                  <div className="rc-px-1">
                    <MdVisibility className="text-success" size={24} />
                  </div>
                </>
              ) : (
                <div className="rc-text-muted">
                  <MdVisibilityOff size={24} />
                </div>
              )}
              {anno?.visibleToUsers?.length > 0 && (
                <>
                  <div className="rc-px-1">
                    <MdPerson className="text-success" size={24} />
                    <sup>{anno?.visibleToUsers?.length}</sup>
                  </div>
                </>
              )}
              {anno?.visibleInClouds?.length > 0 && (
                <>
                  <div className="rc-px-1">
                    <MdCloud className="text-success" size={24} />
                    <sup>{anno?.visibleInClouds?.length}</sup>
                  </div>
                </>
              )}
            </div>
            <div className="rc-flex">
              {
                <div className="rc-relative">
                  <Button
                    variant={commentsOpen ? 'dark' : 'light'}
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setCommentsOpen((s) => !s);
                    }}
                  >
                    <MdComment size={24} />
                  </Button>

                  {props.comments?.length > 0 && (
                    <div className="rc-text-xs rc-absolute rc-flex rc-justify-center rc-items-center rc-w-4 rc-h-4 bg-primary rc-rounded-full rc-text-light rc--top-3 rc--right-2 rc-leading-4">
                      {props.comments?.length}
                    </div>
                  )}

                  <div className="rc-px-1" />
                </div>
              }
              <div className="rc-px-1" />
              {props.onEditAnnotation &&
                (props.user?.id === anno?.author ||
                  props.user?.role === 'Teacher' ||
                  props.user?.role === 'Admin') && (
                  <Button
                    variant="light"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      anno && props.onEditAnnotation(anno);
                    }}
                  >
                    <MdEdit size={24} />
                  </Button>
                )}
              <div className="rc-px-1" />

              {props.onDeleteAnnotation &&
                (props.user?.id === anno?.author ||
                  props.user?.role === 'Teacher' ||
                  props.user?.role === 'Admin') && (
                  <ButtonWithConfirmation
                    confirmText="Delete"
                    confirmVariant="danger"
                    title="Delete Annotation"
                    message="Are you sure you want to delete this annotation?"
                    onToggleExtra={(e) => e?.stopPropagation()}
                    onConfirm={() => anno && props.onDeleteAnnotation(anno.id)}
                    renderButton={
                      <Button variant="light" size="sm">
                        <MdDelete size={24} />
                      </Button>
                    }
                  />
                )}
            </div>
          </div>
        </div>

        <Collapse isOpen={commentsOpen} timeout={250}>
          <div className="rc-px-4">
            <hr className="rc-m-0" />
            <div className="rc-py-4">
              <div className="rc-w-full" style={{ minHeight: '200px' }}>
                {props.comments.map((comment) => (
                  <ConnectedUserComment comment={comment} key={comment.id} />
                ))}
              </div>
              <div className="rc-py-2">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    props.onSubmitComment();
                  }}
                >
                  <Input
                    name="add-comment"
                    placeholder="Write a comment..."
                    onChange={props.onCommentTextChange}
                    value={props.newCommentText}
                    autoCompleteOff
                  />
                </Form>
              </div>
            </div>
          </div>
        </Collapse>
      </div>
    </div>
  );
};
